<template>
  <div>
    <transition name="slide-fade">
      <div
        v-if="showStatus && isVisible"
        class="hardware-status-new"
        :class="statusClass"
      >
        <!-- UHB Not Available - Clean Design -->
        <div v-if="!uhbAvailable && !isInitializing && !collapsed" class="uhb-warning">
          <div class="warning-content">
            <v-icon small class="warning-icon">mdi-alert-circle</v-icon>
            <div class="warning-info">
              <div class="warning-title">Hardware Bridge Required</div>
              <v-btn
                text
                small
                color="primary"
                @click="openDownloadLink"
                class="download-link"
              >
                Download & Install
              </v-btn>
            </div>
          </div>
          <v-btn
            icon
            small
            class="close-button"
            @click="hideWidget"
          >
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Expandable Categories Design -->
        <div v-else class="categories-container">
          <!-- Header with status and controls -->
          <div class="categories-header" :class="{ 'clickable': collapsed }" @click="collapsed && toggleCollapse()">
            <div class="status-indicator">
              <span class="status-dot" :class="`status-${statusText.toLowerCase().replace(' ', '-')}`"></span>
              <span class="status-label">{{ statusText }}</span>
            </div>

            <div class="header-actions">
              <v-btn
                icon
                x-small
                @click="refreshDevices"
                title="Refresh devices"
                class="action-btn"
              >
                <v-icon x-small>mdi-refresh</v-icon>
              </v-btn>
              <v-btn
                icon
                x-small
                @click.stop="toggleCollapse"
                :title="collapsed ? 'Expand widget' : 'Collapse widget'"
                class="action-btn collapse-btn"
              >
                <v-icon x-small>
                  {{ collapsed ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
              </v-btn>
              <v-btn
                icon
                x-small
                @click="hideWidget"
                title="Hide widget"
                class="action-btn"
              >
                <v-icon x-small>mdi-close</v-icon>
              </v-btn>
            </div>
          </div>

          <!-- Category Rows -->
          <div v-if="!collapsed && (hasDevices || uhbAvailable)" class="categories-list">
            <!-- Scale Category -->
            <div class="category-item" :class="{ 'expanded': expandedCategories.scale }">
              <div class="category-row" @click="!scaleCategory.hasDevices || toggleCategoryExpansion('scale')">
                <div class="category-icon">
                  <v-icon :color="scaleCategory.statusColor">mdi-scale-bathroom</v-icon>
                </div>

                <div class="category-info">
                  <div class="category-name">Scale</div>
                  <div class="category-details">
                    <span class="category-status" :class="`status-${scaleCategory.statusColor}`">
                      {{ scaleCategory.status }}
                    </span>
                    <span class="category-primary">{{ scaleCategory.primaryInfo }}</span>
                    <span v-if="scaleCategory.secondaryInfo" class="category-secondary">
                      {{ scaleCategory.secondaryInfo }}
                    </span>
                  </div>
                </div>

                <div class="category-actions">
                  <v-btn
                    v-if="scaleCategory.hasDevices"
                    icon
                    x-small
                    @click.stop="toggleDeviceList('scale')"
                    :title="showDeviceList.scale ? 'Hide devices' : 'Show devices'"
                    class="device-list-btn"
                  >
                    <v-icon x-small>mdi-format-list-bulleted</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="scaleCategory.hasDevices && !showDeviceList.scale"
                    icon
                    x-small
                    @click.stop="toggleCategoryExpansion('scale')"
                    :title="expandedCategories.scale ? 'Collapse' : 'Expand actions'"
                  >
                    <v-icon x-small>
                      {{ expandedCategories.scale ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                    </v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- Scale Device List -->
              <transition name="expand">
                <div v-if="showDeviceList.scale && scaleCategory.hasDevices" class="device-list-panel">
                  <div class="device-list-header">
                    <span class="device-count">{{ scaleCategory.totalCount }} Scale{{ scaleCategory.totalCount !== 1 ? 's' : '' }}</span>
                  </div>
                  <div
                    v-for="scale in scales"
                    :key="scale.id"
                    class="device-list-item"
                    :class="{ 'connected': scale.isConnected }"
                  >
                    <div class="device-info">
                      <div class="device-name">{{ scale.product || scale.manufacturer || scale.id }}</div>
                      <div class="device-details">
                        <span class="device-id">ID: {{ scale.id }}</span>
                        <v-chip
                          x-small
                          :color="scale.isConnected ? 'success' : 'grey'"
                          :outlined="!scale.isConnected"
                        >
                          {{ scale.isConnected ? 'Connected' : 'Available' }}
                        </v-chip>
                      </div>
                    </div>
                    <div class="device-actions">
                      <v-btn
                        v-if="!scale.isConnected"
                        x-small
                        color="primary"
                        outlined
                        :loading="isDeviceLoading(scale.id) && getDeviceLoadingOperation(scale.id) === 'connecting'"
                        :disabled="isDeviceLoading(scale.id)"
                        @click="connectToDevice(scale, 'scale')"
                      >
                        Connect
                      </v-btn>
                      <v-btn
                        v-else
                        x-small
                        color="error"
                        outlined
                        :loading="isDeviceLoading(scale.id) && getDeviceLoadingOperation(scale.id) === 'disconnecting'"
                        :disabled="isDeviceLoading(scale.id)"
                        @click="disconnectFromDevice(scale, 'scale')"
                      >
                        Disconnect
                      </v-btn>
                    </div>
                  </div>
                </div>
              </transition>

              <!-- Scale Actions Panel -->
              <transition name="expand">
                <div v-if="expandedCategories.scale && scaleCategory.hasDevices" class="actions-panel">
                  <v-btn
                    v-if="scaleCategory.connectedCount > 0"
                    small
                    outlined
                    color="primary"
                    @click="handleScaleAction('testScale')"
                    class="action-button"
                  >
                    <v-icon small left>mdi-test-tube</v-icon>
                    Test Scale
                  </v-btn>
                  <v-btn
                    v-if="scaleCategory.connectedCount > 0"
                    small
                    outlined
                    color="success"
                    @click="handleScaleAction('zeroScale')"
                    class="action-button"
                    :loading="isZeroingScale"
                  >
                    <v-icon small left>mdi-refresh</v-icon>
                    Zero Scale
                  </v-btn>
                  <v-btn
                    small
                    outlined
                    color="info"
                    @click="handleScaleAction('weightLog')"
                    class="action-button"
                  >
                    <v-icon small left>mdi-chart-line</v-icon>
                    Weight Log
                  </v-btn>
                </div>
              </transition>
            </div>

            <!-- Printer Category -->
            <div class="category-item" :class="{ 'expanded': expandedCategories.printer }">
              <div class="category-row" @click="!printerCategory.hasDevices || toggleCategoryExpansion('printer')">
                <div class="category-icon">
                  <v-icon :color="printerCategory.statusColor">mdi-printer</v-icon>
                </div>

                <div class="category-info">
                  <div class="category-name">Printer</div>
                  <div class="category-details">
                    <span class="category-status" :class="`status-${printerCategory.statusColor}`">
                      {{ printerCategory.status }}
                    </span>
                    <span class="category-primary">{{ printerCategory.primaryInfo }}</span>
                    <span v-if="printerCategory.secondaryInfo" class="category-secondary">
                      {{ printerCategory.secondaryInfo }}
                    </span>
                  </div>
                </div>

                <div class="category-actions">
                  <v-btn
                    v-if="printerCategory.hasDevices && printerCategory.totalCount > 1"
                    icon
                    x-small
                    @click.stop="toggleDeviceList('printer')"
                    :title="showDeviceList.printer ? 'Hide devices' : 'Show devices'"
                    class="device-list-btn"
                  >
                    <v-icon x-small>mdi-format-list-bulleted</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="printerCategory.hasDevices && (printerCategory.totalCount === 1 || !showDeviceList.printer)"
                    icon
                    x-small
                    @click.stop="toggleCategoryExpansion('printer')"
                    :title="expandedCategories.printer ? 'Collapse' : 'Expand actions'"
                  >
                    <v-icon x-small>
                      {{ expandedCategories.printer ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                    </v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- Printer Device List -->
              <transition name="expand">
                <div v-if="showDeviceList.printer && printerCategory.hasDevices" class="device-list-panel">
                  <div class="device-list-header">
                    <span class="device-count">{{ printerCategory.totalCount }} Printer{{ printerCategory.totalCount !== 1 ? 's' : '' }}</span>
                  </div>
                  <div
                    v-for="printer in printers"
                    :key="printer.id"
                    class="device-list-item"
                    :class="{ 'connected': printer.isConnected }"
                  >
                    <div class="device-info">
                      <div class="device-name">{{ printer.name || printer.id }}</div>
                      <div class="device-details">
                        <span class="device-id">ID: {{ printer.id }}</span>
                        <v-chip
                          x-small
                          :color="printer.isConnected ? 'success' : 'grey'"
                          :outlined="!printer.isConnected"
                        >
                          {{ printer.isConnected ? 'Connected' : 'Available' }}
                        </v-chip>
                      </div>
                    </div>
                    <div class="device-actions">
                      <v-btn
                        v-if="!printer.isConnected"
                        x-small
                        color="primary"
                        outlined
                        :loading="isDeviceLoading(printer.id) && getDeviceLoadingOperation(printer.id) === 'connecting'"
                        :disabled="isDeviceLoading(printer.id)"
                        @click="connectToDevice(printer, 'printer')"
                      >
                        Connect
                      </v-btn>
                      <v-btn
                        v-else
                        x-small
                        color="grey"
                        outlined
                        :loading="isDeviceLoading(printer.id) && getDeviceLoadingOperation(printer.id) === 'disconnecting'"
                        :disabled="isDeviceLoading(printer.id)"
                        @click="disconnectFromDevice(printer, 'printer')"
                      >
                        Disconnect
                      </v-btn>
                    </div>
                  </div>
                </div>
              </transition>

              <!-- Printer Actions Panel -->
              <transition name="expand">
                <div v-if="expandedCategories.printer && printerCategory.hasDevices" class="actions-panel">
                  <v-btn
                    small
                    outlined
                    color="primary"
                    @click="handlePrinterAction('testPrint')"
                    class="action-button"
                  >
                    <v-icon small left>mdi-printer-check</v-icon>
                    Test Print
                  </v-btn>
                  <v-btn
                    small
                    outlined
                    color="grey"
                    @click="handlePrinterAction('settings')"
                    class="action-button"
                  >
                    <v-icon small left>mdi-cog</v-icon>
                    Settings
                  </v-btn>
                </div>
              </transition>
            </div>

            <!-- Scanner Category -->
            <div class="category-item" :class="{ 'expanded': expandedCategories.scanner }">
              <div class="category-row" @click="!scannerCategory.hasDevices || toggleCategoryExpansion('scanner')">
                <div class="category-icon">
                  <v-icon :color="scannerCategory.statusColor">mdi-barcode-scan</v-icon>
                </div>

                <div class="category-info">
                  <div class="category-name">Scanner</div>
                  <div class="category-details">
                    <span class="category-status" :class="`status-${scannerCategory.statusColor}`">
                      {{ scannerCategory.status }}
                    </span>
                    <span class="category-primary">{{ scannerCategory.primaryInfo }}</span>
                  </div>
                </div>

                <div class="category-actions">
                  <v-btn
                    v-if="scannerCategory.hasDevices"
                    icon
                    x-small
                    @click.stop="toggleDeviceList('scanner')"
                    :title="showDeviceList.scanner ? 'Hide devices' : 'Show devices'"
                    class="device-list-btn"
                  >
                    <v-icon x-small>mdi-format-list-bulleted</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="scannerCategory.hasDevices && !showDeviceList.scanner"
                    icon
                    x-small
                    @click.stop="toggleCategoryExpansion('scanner')"
                    :title="expandedCategories.scanner ? 'Collapse' : 'Expand actions'"
                  >
                    <v-icon x-small>
                      {{ expandedCategories.scanner ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                    </v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- Scanner Device List -->
              <transition name="expand">
                <div v-if="showDeviceList.scanner && scannerCategory.hasDevices" class="device-list-panel">
                  <div class="device-list-header">
                    <span class="device-count">{{ scannerCategory.totalCount }} Scanner{{ scannerCategory.totalCount !== 1 ? 's' : '' }}</span>
                  </div>
                  <div
                    v-for="scanner in scanners"
                    :key="scanner.id"
                    class="device-list-item"
                    :class="{ 'connected': scanner.isConnected }"
                  >
                    <div class="device-info">
                      <div class="device-name">{{ scanner.name || scanner.id }}</div>
                      <div class="device-details">
                        <span class="device-id">ID: {{ scanner.id }}</span>
                        <v-chip
                          x-small
                          :color="scanner.isConnected ? 'success' : 'grey'"
                          :outlined="!scanner.isConnected"
                        >
                          {{ scanner.isConnected ? 'Connected' : 'Available' }}
                        </v-chip>
                      </div>
                    </div>
                    <div class="device-actions">
                      <v-btn
                        v-if="!scanner.isConnected"
                        x-small
                        color="primary"
                        outlined
                        :loading="isDeviceLoading(scanner.id) && getDeviceLoadingOperation(scanner.id) === 'connecting'"
                        :disabled="isDeviceLoading(scanner.id)"
                        @click="connectToDevice(scanner, 'scanner')"
                      >
                        Connect
                      </v-btn>
                      <v-btn
                        v-else
                        x-small
                        color="grey"
                        outlined
                        :loading="isDeviceLoading(scanner.id) && getDeviceLoadingOperation(scanner.id) === 'disconnecting'"
                        :disabled="isDeviceLoading(scanner.id)"
                        @click="disconnectFromDevice(scanner, 'scanner')"
                      >
                        Disconnect
                      </v-btn>
                    </div>
                  </div>
                </div>
              </transition>

              <!-- Scanner Actions Panel -->
              <transition name="expand">
                <div v-if="expandedCategories.scanner && scannerCategory.hasDevices" class="actions-panel">
                  <v-btn
                    small
                    outlined
                    color="primary"
                    @click="handleScannerAction('testScan')"
                    class="action-button"
                  >
                    <v-icon small left>mdi-barcode-scan</v-icon>
                    Test Scan
                  </v-btn>
                </div>
              </transition>

            </div>
          </div>
        </div>

        <!-- Recent Activity (Minimal) -->
        <transition name="activity-container-slide">
          <div v-if="!collapsed && recentActivity" class="recent-activity-container">
            <div class="recent-activity">
              <v-icon x-small color="white">mdi-check</v-icon>
              <span class="activity-text">{{ recentActivity.text }}</span>
            </div>
          </div>
        </transition>
      </div>
    </transition>

    <!-- Enhanced Scan Test Dialog -->
    <v-dialog v-model="showScanTestDialog" max-width="450">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left color="primary">mdi-barcode-scan</v-icon>
          Test Scanner
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="testScanValue"
            label="Barcode Data"
            placeholder="Enter barcode value (e.g., *12345* or PT12345)"
            outlined
            dense
            autofocus
            @keyup.enter="submitTestScan"
            prepend-inner-icon="mdi-barcode"
          ></v-text-field>

          <div class="mt-3">
            <div class="text-subtitle2 mb-2">Common Formats:</div>
            <div class="text-caption grey--text mb-1">• Pick Ticket: PT12345</div>
            <div class="text-caption grey--text mb-1">• Product: *123456789*</div>
            <div class="text-caption grey--text">• Custom: Any alphanumeric string</div>
          </div>

          <v-alert
            v-if="scannerCategory.connectedCount === 0"
            type="info"
            dense
            outlined
            class="mt-3"
          >
            No scanners connected. This will simulate a scan event.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showScanTestDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="submitTestScan"
            :disabled="!testScanValue.trim()"
          >
            <v-icon small left>mdi-send</v-icon>
            Test Scan
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Print Test Dialog -->
    <v-dialog v-model="showPrintTestDialog" max-width="400">
      <v-card>
        <v-card-title>Test Print</v-card-title>
        <v-card-text>
          <v-select
            v-model="selectedPrinter"
            :items="availablePrinters"
            item-text="name"
            item-value="id"
            label="Select Printer"
            outlined
          ></v-select>
          <div class="text-caption grey--text">
            Send a test print to verify printer connectivity and functionality.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showPrintTestDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="submitTestPrint">Test Print</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Printer Settings Dialog -->
    <v-dialog v-model="showPrinterSettingsDialog" max-width="500">
      <v-card>
        <v-card-title>
          <v-icon left>mdi-printer-settings</v-icon>
          Printer Settings
        </v-card-title>
        <v-card-text>
          <div class="text-subtitle-2 mb-3">Auto-Print Configuration</div>
          <div class="text-caption grey--text mb-4">
            Configure which printer to use for automatically printing shipping labels.
          </div>

          <v-select
            v-model="selectedAutoPrintPrinter"
            :items="availablePrinters"
            item-text="name"
            item-value="id"
            label="Select Printer for Auto-Print"
            outlined
            :disabled="!availablePrinters.length"
          >
            <template v-slot:selection="{ item }">
              <div class="d-flex align-center">
                <v-icon
                  small
                  :color="item.isConnected ? 'success' : 'grey'"
                  class="mr-2"
                >
                  {{ item.isConnected ? 'mdi-printer' : 'mdi-printer-off' }}
                </v-icon>
                {{ item.name }}
                <v-chip
                  v-if="item.isConnected"
                  x-small
                  color="success"
                  text-color="white"
                  class="ml-2"
                >
                  Connected
                </v-chip>
              </div>
            </template>
            <template v-slot:item="{ item }">
              <div class="d-flex align-center">
                <v-icon
                  small
                  :color="item.isConnected ? 'success' : 'grey'"
                  class="mr-2"
                >
                  {{ item.isConnected ? 'mdi-printer' : 'mdi-printer-off' }}
                </v-icon>
                {{ item.name }}
                <v-chip
                  v-if="item.isConnected"
                  x-small
                  color="success"
                  text-color="white"
                  class="ml-2"
                >
                  Connected
                </v-chip>
              </div>
            </template>
          </v-select>

          <v-switch
            v-model="autoPrintEnabled"
            :disabled="!selectedAutoPrintPrinter"
            label="Automatically print shipping labels"
            color="primary"
            class="mt-2"
          ></v-switch>

          <v-select
            v-model="selectedLabelLayout"
            :items="labelLayoutOptions"
            label="Default Label Size"
            outlined
            dense
            class="mt-4"
            hint="Select the default label size for shipping labels"
            persistent-hint
          ></v-select>

          <v-alert
            v-if="!availablePrinters.length"
            type="info"
            dense
            outlined
            class="mt-3"
          >
            No printers available. Make sure your printers are connected via the Universal Hardware Bridge.
          </v-alert>

          <v-alert
            v-else-if="selectedAutoPrintPrinter && !availablePrinters.find(p => p.id === selectedAutoPrintPrinter)?.isConnected"
            type="warning"
            dense
            outlined
            class="mt-3"
          >
            Selected printer is not currently connected. Auto-print may not work until the printer is reconnected.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showPrinterSettingsDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="savePrinterSettings">Save Settings</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Hardware Settings Dialog -->
    <v-dialog v-model="showSettings" max-width="500">
      <v-card>
        <v-card-title>Hardware Settings</v-card-title>
        <v-card-text>
          <v-switch
            v-model="widgetVisible"
            label="Show hardware status widget"
            @change="saveVisibilitySetting"
          ></v-switch>
          <v-divider class="my-3"></v-divider>
          <div class="text-subtitle2 mb-2">Universal Hardware Bridge</div>
          <div class="text-caption grey--text mb-3">
            Status: {{ uhbAvailable ? 'Connected' : 'Not Available' }}
          </div>
          <v-btn
            v-if="!uhbAvailable"
            outlined
            small
            @click="openDownloadLink"
          >
            <v-icon small left>mdi-download</v-icon>
            Download UHB
          </v-btn>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showSettings = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Test Scale Dialog -->
    <v-dialog v-model="showTestScaleDialog" max-width="400">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left color="primary">mdi-scale-bathroom</v-icon>
          Test Scale
        </v-card-title>
        <v-card-text>
          <div class="text-center py-4">
            <div class="d-flex align-center justify-center mb-3">
              <v-chip small color="success" dark class="mr-2">
                <v-icon small left>mdi-circle</v-icon>
                LIVE READING
              </v-chip>
            </div>

            <div class="weight-display-large">
              <div class="weight-value">
                {{ currentWeight ? currentWeight.value : '0.0' }}
              </div>
              <div class="weight-unit">
                {{ currentWeight ? currentWeight.unit : 'lbs' }}
              </div>
            </div>

            <div class="mt-3 text-body-2 grey--text">
              Current weight reading from connected scale
            </div>

            <div class="mt-2 text-caption grey--text">
              Place or remove items to see live weight changes
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showTestScaleDialog = false">Close</v-btn>
          <v-btn
            color="success"
            @click="zeroScale()"
            :loading="isZeroingScale"
          >
            <v-icon small left>mdi-refresh</v-icon>
            Zero Scale
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Weight Log Dialog -->
    <v-dialog v-model="showWeightLogDialog" max-width="500">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left color="info">mdi-chart-line</v-icon>
          Weight Log
          <v-spacer></v-spacer>
          <v-btn icon small @click="clearWeightLog" :disabled="weightHistory.length === 0">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div v-if="weightHistory.length === 0" class="text-center py-4 grey--text">
            <v-icon large color="grey lighten-1">mdi-history</v-icon>
            <div class="mt-2">No weight readings recorded</div>
            <div class="text-caption">Use \"Test Scale\" or \"Zero Scale\" to add entries</div>
          </div>

          <div v-else class="weight-log-list">
            <div
              v-for="(entry, index) in weightHistory"
              :key="index"
              class="weight-log-entry"
            >
              <div class="d-flex justify-space-between align-center">
                <div>
                  <div class="weight-value-small">{{ entry.weight }}</div>
                  <div class="text-caption grey--text">{{ entry.action }}</div>
                </div>
                <div class="text-caption grey--text text-right">
                  {{ entry.displayTime }}
                </div>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn text color="error" @click="clearWeightLog" :disabled="weightHistory.length === 0">
            Clear Log
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="showWeightLogDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { getScannerEventService } from '@/services/ScannerEventService';
import { getHardwareService } from '@/services/HardwareIntegrationService';
import { getKeyboardWedgeScanner } from '@/services/KeyboardWedgeScanner';
import { TestPrintService } from '@/services/TestPrintService';
import { getPrinterConfiguration, PrinterConfiguration } from '@/services/PrinterConfigurationService';

export default {
  name: 'HardwareStatus',
  data() {
    return {
      hardwareService: null,
      scannerService: null,
      printerConfigService: null,
      scales: [],
      printers: [],
      scanners: [],
      scalesMap: new Map(),
      printersMap: new Map(),
      scannersMap: new Map(),
      connectedDevices: new Map(),
      recentActivity: null,
      activityTimeout: null,
      showStatus: false,
      minimized: false,
      collapsed: false,
      uhbError: null,
      isInitializing: false,
      autoRefreshTimer: null,
      autoRefreshInterval: 30000, // CHANGED: 30 seconds (was 5 seconds) to reduce server load
      lastFullRefresh: 0,
      fullRefreshInterval: 60000, // CHANGED: 60 seconds (was 30 seconds) to reduce server load
      quickRefreshInterval: 10000, // CHANGED: 10 seconds (was 3 seconds) for initial discovery
      isWaitingForWeight: false,
      currentWeight: null,
      weightHistory: [],
      maxLogEntries: 50,
      isZeroingScale: false,
      showScanTestDialog: false,
      showPrintTestDialog: false,
      showPrinterSettingsDialog: false,
      showTestScaleDialog: false,
      showWeightLogDialog: false,
      showSettings: false,
      testBarcode: '',
      selectedPrinter: null,
      isPrinting: false, // Prevent infinite print loops
      // Auto-print settings
      autoPrintEnabled: false,
      selectedAutoPrintPrinter: null,
      selectedLabelLayout: '4x6',
      labelLayoutOptions: [
        { text: '4x6 - Standard Shipping', value: '4x6' },
        { text: '4x8 - Extended Shipping', value: '4x8' },
        { text: '8.5x11 (Letter)', value: 'letter' }
      ],
      widgetVisible: true,
      isVisible: true,
      // Expandable categories state
      expandedCategories: {
        scale: false,
        printer: false,
        scanner: false
      },
      // Device drill-down state
      showDeviceList: {
        scale: false,
        printer: false,
        scanner: false
      },
      testScanValue: '',
      // Loading states for device operations
      deviceOperationLoading: {}, // deviceId -> operation type (using reactive object instead of Map)
      // Configuration service subscription
      configurationUnsubscribe: null
    };
  },
  computed: {
    uhbAvailable() {
      return this.hardwareService?.isConnected && !this.uhbError && !this.isInitializing;
    },
    hasDevices() {
      return this.scales.length > 0 || this.printers.length > 0 || this.scanners.length > 0;
    },
    availablePrinters() {
      return this.printers.map(printer => ({
        id: printer.id,
        name: printer.name || printer.id,
        isConnected: printer.isConnected
      }));
    },
    selectedAutoPrinterName() {
      if (!this.selectedAutoPrintPrinter) return 'None selected';
      const printer = this.availablePrinters.find(p => p.id === this.selectedAutoPrintPrinter);
      return printer?.name || 'Unknown Printer';
    },
    statusClass() {
      return {
        'connected': this.uhbAvailable && this.hasConnectedDevices,
        'disconnected': !this.uhbAvailable || (!this.hasConnectedDevices && this.hasDevices),
        'no-devices': !this.hasDevices && this.uhbAvailable,
        'minimized': this.minimized,
        'collapsed': this.collapsed,
        'has-error': this.uhbError !== null,
        'initializing': this.isInitializing,
        'warning': !this.uhbAvailable && !this.isInitializing
      };
    },
    statusText() {
      if (this.isInitializing) {
        return 'Initializing...';
      }
      if (!this.uhbAvailable) {
        return 'Hardware Offline';
      }
      if (!this.hasDevices) {
        return 'No Hardware';
      }
      if (this.hasConnectedDevices) {
        return 'Hardware Ready';
      }
      return 'Hardware Available';
    },
    allDevices() {
      const devices = [];

      // Add scales
      this.scales.forEach(scale => {
        devices.push({
          id: scale.id,
          name: scale.product || scale.manufacturer || 'Scale',
          isConnected: scale.isConnected,
          icon: 'mdi-scale-bathroom',
          type: 'scale'
        });
      });

      // Add printers
      this.printers.forEach(printer => {
        devices.push({
          id: printer.id,
          name: printer.name || 'Printer',
          isConnected: printer.isConnected,
          icon: 'mdi-printer',
          type: 'printer'
        });
      });

      // Add scanners
      this.scanners.forEach(scanner => {
        devices.push({
          id: scanner.id,
          name: scanner.name || 'Scanner',
          isConnected: scanner.isConnected,
          icon: 'mdi-barcode-scan',
          type: 'scanner'
        });
      });

      return devices.sort((a, b) => {
        // Sort connected devices first
        if (a.isConnected !== b.isConnected) {
          return b.isConnected ? 1 : -1;
        }
        return a.name.localeCompare(b.name);
      });
    },
    connectedScales() {
      return this.scales.filter(s => s.isConnected).length;
    },
    connectedPrinters() {
      return this.printers.filter(p => p.isConnected).length;
    },
    connectedScanners() {
      return this.scanners.filter(s => s.isConnected).length;
    },
    hasConnectedDevices() {
      return this.connectedScales > 0 || this.connectedPrinters > 0 || this.connectedScanners > 0;
    },
    hasAvailableScale() {
      return this.scales.length > 0;
    },
    hasConnectedScale() {
      return this.connectedScales > 0;
    },
    hasAvailablePrinter() {
      return this.printers.length > 0;
    },
    hasConnectedPrinter() {
      return this.connectedPrinters > 0;
    },
    hasAvailableScanner() {
      return this.scanners.length > 0;
    },
    hasConnectedScanner() {
      return this.connectedScanners > 0;
    },
    hardwareEnabled() {
      const enabled = localStorage.getItem('hardware_enabled');
      return enabled !== 'false';
    },

    // Category-specific computed properties for the new design
    scaleCategory() {
      const connectedScales = this.scales.filter(s => s.isConnected);
      const availableScales = this.scales.length;

      // Debug logging for scale status issue
      if (this.currentWeight && availableScales === 0) {
        console.warn('[HardwareStatus] SCALE STATUS ISSUE: Have weight readings but no scales in array!', {
          scalesArrayLength: availableScales,
          scalesArray: this.scales,
          currentWeight: this.currentWeight,
          scalesMapSize: this.scalesMap.size,
          scalesMapEntries: Array.from(this.scalesMap.entries())
        });
      }

      return {
        hasDevices: availableScales > 0,
        connectedCount: connectedScales.length,
        totalCount: availableScales,
        status: connectedScales.length > 0 ? 'Connected' :
                availableScales > 0 ? 'Available' : 'None',
        statusColor: connectedScales.length > 0 ? 'success' :
                    availableScales > 0 ? 'warning' : 'grey',
        primaryInfo: connectedScales.length > 0 && this.currentWeight ?
                    `${this.formatWeight(this.currentWeight.value)} ${this.currentWeight.unit}` :
                    availableScales > 0 ? `${availableScales} Scale${availableScales !== 1 ? 's' : ''}` : 'None',
        secondaryInfo: connectedScales.length > 0 && this.currentWeight?.isStable === false ? 'Stabilizing...' : null,
        connectedDevices: connectedScales
      };
    },

    printerCategory() {
      const connectedPrinters = this.printers.filter(p => p.isConnected);
      const availablePrinters = this.printers.length;

      return {
        hasDevices: availablePrinters > 0,
        connectedCount: connectedPrinters.length,
        totalCount: availablePrinters,
        status: connectedPrinters.length > 0 ? `${connectedPrinters.length} Ready` :
                availablePrinters > 0 ? `${availablePrinters} Available` : 'None',
        statusColor: connectedPrinters.length > 0 ? 'success' :
                    availablePrinters > 0 ? 'warning' : 'grey',
        primaryInfo: availablePrinters > 0 ?
                    (connectedPrinters[0]?.name || this.printers[0]?.name || 'Printer') : 'None',
        secondaryInfo: availablePrinters > 1 ? `+${availablePrinters - 1} more` : null,
        connectedDevices: connectedPrinters
      };
    },

    scannerCategory() {
      const connectedScanners = this.scanners.filter(s => s.isConnected);
      const availableScanners = this.scanners.length;

      return {
        hasDevices: availableScanners > 0,
        connectedCount: connectedScanners.length,
        totalCount: availableScanners,
        status: connectedScanners.length > 0 ? 'Connected' :
                availableScanners > 0 ? 'Available' : 'None',
        statusColor: connectedScanners.length > 0 ? 'success' :
                    availableScanners > 0 ? 'warning' : 'grey',
        primaryInfo: availableScanners > 0 ?
                    (connectedScanners[0]?.name || this.scanners[0]?.name || 'Scanner') : 'None',
        secondaryInfo: null,
        connectedDevices: connectedScanners
      };
    }
  },
  async mounted() {
    // Initialize printer configuration service
    this.printerConfigService = getPrinterConfiguration();

    // Load visibility setting
    this.loadVisibilitySetting();

    // Load printer settings
    this.loadPrinterSettings();

    // Subscribe to configuration changes from other components
    this.configurationUnsubscribe = this.printerConfigService.onConfigurationChange((config) => {
      console.log('[HardwareStatus] Printer configuration changed:', config);
      this.autoPrintEnabled = config.autoPrintLabels;
      this.selectedAutoPrintPrinter = config.labelPrinterId;
      this.selectedLabelLayout = config.defaultLabelLayout || '4x6';

      // Validate that the selected printer still exists
      if (this.selectedAutoPrintPrinter && this.availablePrinters.length > 0) {
        const printerExists = this.availablePrinters.some(p => p.id === this.selectedAutoPrintPrinter);
        if (!printerExists) {
          console.log('[HardwareStatus] Selected printer no longer available:', this.selectedAutoPrintPrinter);
        }
      }
    });

    // Listen for hardware widget toggle from Navigation component
    this.$root.$on('hardware-widget-toggle', (visible) => {
      this.widgetVisible = visible;
      this.isVisible = visible;

      if (visible && this.$store.getters.isLoggedIn && this.hardwareEnabled) {
        this.initializeServices();
      } else {
        this.cleanup();
      }
    });

    // Listen for requests to show hardware status with printer settings
    this.$bus?.$on('hardware-status-show', (options) => {
      this.widgetVisible = true;
      this.isVisible = true;
      this.showStatus = true;

      if (options?.openPrinterSettings) {
        // Small delay to ensure the dialog is open first
        setTimeout(() => {
          this.loadPrinterSettings();
          this.showPrinterSettingsDialog = true;
        }, 300);
      }
    });

    // Only initialize if user is logged in and hardware is enabled
    if (this.$store.getters.isLoggedIn && this.hardwareEnabled && this.isVisible) {
      await this.initializeServices();
    }

    // Watch for login state changes
    this.$store.watch(
      (state) => state.authentication?.isLoggedIn,
      async (isLoggedIn) => {
        if (isLoggedIn && this.hardwareEnabled && this.isVisible) {
          await this.initializeServices();
        } else {
          this.cleanup();
        }
      }
    );
  },
  beforeDestroy() {
    this.cleanup();
    // Clean up event listeners
    this.$root.$off('hardware-widget-toggle');
    this.$bus?.$off('hardware-status-show');

    // Clean up configuration subscription
    if (this.configurationUnsubscribe) {
      this.configurationUnsubscribe();
      this.configurationUnsubscribe = null;
    }
  },
  methods: {
    async initializeServices() {
      if (this.isInitializing) return;

      try {
        this.isInitializing = true;
        this.showStatus = true;

        // Initialize hardware service (force UHB mode)
        this.hardwareService = getHardwareService(true);
        await this.hardwareService.initialize();

        // Initialize scanner service
        this.scannerService = getScannerEventService();
        await this.scannerService.initialize();

        // Discover devices
        await this.discoverAllDevices();

        // Set up event listeners
        this.setupEventListeners();

        // Start auto-refresh mechanism
        this.startAutoRefresh();

        console.log('[HardwareStatus] Hardware services initialized');
      } catch (error) {
        console.error('[HardwareStatus] Failed to initialize:', error);
        this.uhbError = error.message || 'Failed to initialize hardware';
      } finally {
        this.isInitializing = false;
      }
    },

    async discoverAllDevices() {
      try {
        console.log('[HardwareStatus] Starting device discovery...');

        // Discover scales
        const scales = await this.hardwareService.discoverScales();
        console.log('[HardwareStatus] discoverScales() returned:', scales);
        this.scalesMap.clear();
        this.scales.splice(0, this.scales.length);
        scales.forEach(scale => {
          this.scalesMap.set(scale.id, scale);
          this.scales.push(scale);
        });

        // Discover printers
        const printers = await this.hardwareService.discoverPrinters();
        console.log('[HardwareStatus] discoverPrinters() returned:', printers);
        this.printersMap.clear();
        this.printers.splice(0, this.printers.length);
        printers.forEach(printer => {
          this.printersMap.set(printer.id, printer);
          this.printers.push(printer);
        });

        // Discover scanners
        const scanners = await this.hardwareService.discoverScanners();
        console.log('[HardwareStatus] discoverScanners() returned:', scanners);
        this.scannersMap.clear();
        this.scanners.splice(0, this.scanners.length);
        scanners.forEach(scanner => {
          this.scannersMap.set(scanner.id, scanner);
          this.scanners.push(scanner);
        });

        console.log('[HardwareStatus] Discovered devices:', {
          scales: scales.length,
          printers: printers.length,
          scanners: scanners.length
        });

        // Update last refresh timestamp
        this.lastFullRefresh = Date.now();

        // Attempt auto-connect for newly discovered devices
        setTimeout(() => {
          this.autoConnectDevices();
        }, 1000);
      } catch (error) {
        console.error('[HardwareStatus] Failed to discover devices:', error);
      }
    },

    setupEventListeners() {
      // UHB Connection events
      this.hardwareService.on('connected', this.onUHBConnected);
      this.hardwareService.on('disconnected', this.onUHBDisconnected);
      this.hardwareService.on('devices-updated', this.onDevicesUpdated);

      // Scale events
      this.hardwareService.on('scale-connected', this.onScaleConnected);
      this.hardwareService.on('scale-disconnected', this.onScaleDisconnected);
      this.hardwareService.on('weight', this.onWeightUpdate);

      // Printer events
      this.hardwareService.on('printer-connected', this.onPrinterConnected);
      this.hardwareService.on('printer-disconnected', this.onPrinterDisconnected);
      this.hardwareService.on('print-completed', this.onPrintCompleted);

      // Scanner events
      this.hardwareService.on('scanner-connected', this.onScannerConnected);
      this.hardwareService.on('scanner-disconnected', this.onScannerDisconnected);

      // Scanner data events
      this.scannerService.on('pick-ticket-scanned', this.onPickTicketScanned);
      this.scannerService.on('barcode-scanned', this.onBarcodeScanned);
    },

    // Event Handlers
    onUHBConnected(data) {
      console.log('[HardwareStatus] UHB connected:', data);
      this.uhbError = null;

      // Immediately discover devices when UHB connects
      this.discoverAllDevices();

      // Restart auto-refresh when UHB reconnects
      if (!this.autoRefreshTimer) {
        this.startAutoRefresh();
      }

      // Schedule additional discovery attempts to ensure devices are found quickly
      setTimeout(() => {
        console.log('[HardwareStatus] Secondary device discovery after UHB connection...');
        this.discoverAllDevices();
      }, 1000);

      setTimeout(() => {
        console.log('[HardwareStatus] Tertiary device discovery after UHB connection...');
        this.discoverAllDevices();
      }, 3000);
    },

    onUHBDisconnected(error) {
      console.log('[HardwareStatus] UHB disconnected:', error);
      this.uhbError = typeof error === 'string' ? error : 'Connection lost';
      // Stop auto-refresh when UHB disconnects to avoid errors
      this.stopAutoRefresh();
    },

    onDevicesUpdated(data) {
      console.log('[HardwareStatus] Devices updated:', {
        fullRefresh: data.fullRefresh,
        scalesCount: data.scales?.length || 0,
        printersCount: data.printers?.length || 0,
        scannersCount: data.scanners?.length || 0,
        data: data
      });

      // Skip only if the data structure is completely invalid (no device arrays provided at all)
      if (!data.scales && !data.printers && !data.scanners) {
        console.log('[HardwareStatus] Skipping device update - no device arrays provided');
        return;
      }

      console.log('[HardwareStatus] Processing device update...');

      if (data.scales) {
        // Merge scales instead of replacing all
        const existingScaleIds = new Set(this.scales.map(s => s.id));
        const newScaleIds = new Set(data.scales.map(s => s.id));

        // Remove scales that are no longer present
        for (let i = this.scales.length - 1; i >= 0; i--) {
          if (!newScaleIds.has(this.scales[i].id)) {
            this.scalesMap.delete(this.scales[i].id);
            this.scales.splice(i, 1);
          }
        }

        // Add or update scales
        data.scales.forEach(scale => {
          const existingScale = this.scalesMap.get(scale.id);
          if (existingScale) {
            // Update existing scale while preserving loading states
            const updatedScale = { ...scale };
            this.scalesMap.set(scale.id, updatedScale);
            const index = this.scales.findIndex(s => s.id === scale.id);
            if (index >= 0) {
              this.$set(this.scales, index, updatedScale);
            }
          } else {
            // Add new scale
            this.scalesMap.set(scale.id, scale);
            this.scales.push(scale);
          }
        });
      }

      if (data.printers) {
        // Merge printers instead of replacing all
        const existingPrinterIds = new Set(this.printers.map(p => p.id));
        const newPrinterIds = new Set(data.printers.map(p => p.id));

        // Remove printers that are no longer present
        for (let i = this.printers.length - 1; i >= 0; i--) {
          if (!newPrinterIds.has(this.printers[i].id)) {
            this.printersMap.delete(this.printers[i].id);
            this.printers.splice(i, 1);
          }
        }

        // Add or update printers
        data.printers.forEach(printer => {
          const existingPrinter = this.printersMap.get(printer.id);
          if (existingPrinter) {
            // Update existing printer while preserving loading states
            const updatedPrinter = { ...printer };
            this.printersMap.set(printer.id, updatedPrinter);
            const index = this.printers.findIndex(p => p.id === printer.id);
            if (index >= 0) {
              this.$set(this.printers, index, updatedPrinter);
            }
          } else {
            // Add new printer
            this.printersMap.set(printer.id, printer);
            this.printers.push(printer);
          }
        });
      }

      if (data.scanners) {
        // Merge scanners instead of replacing all
        const existingScannerIds = new Set(this.scanners.map(s => s.id));
        const newScannerIds = new Set(data.scanners.map(s => s.id));

        // Remove scanners that are no longer present
        for (let i = this.scanners.length - 1; i >= 0; i--) {
          if (!newScannerIds.has(this.scanners[i].id)) {
            this.scannersMap.delete(this.scanners[i].id);
            this.scanners.splice(i, 1);
          }
        }

        // Add or update scanners
        data.scanners.forEach(scanner => {
          const existingScanner = this.scannersMap.get(scanner.id);
          if (existingScanner) {
            // Update existing scanner while preserving loading states
            const updatedScanner = { ...scanner };
            this.scannersMap.set(scanner.id, updatedScanner);
            const index = this.scanners.findIndex(s => s.id === scanner.id);
            if (index >= 0) {
              this.$set(this.scanners, index, updatedScanner);
            }
          } else {
            // Add new scanner
            this.scannersMap.set(scanner.id, scanner);
            this.scanners.push(scanner);
          }
        });
      }

      // Update last refresh timestamp when devices are updated via events
      this.lastFullRefresh = Date.now();
    },

    onScaleConnected(scale) {
      console.log('[HardwareStatus] Scale connected:', scale);
      const updatedScale = { ...scale, isConnected: true };
      this.scalesMap.set(scale.id, updatedScale);
      // Update the array
      const index = this.scales.findIndex(s => s.id === scale.id);
      if (index >= 0) {
        this.$set(this.scales, index, updatedScale);
      } else {
        // If scale not in array, add it
        this.scales.push(updatedScale);
      }
      this.showRecentActivity('scale', 'mdi-scale', `Scale ${scale.product || scale.id} connected`);

      // Force an immediate weight reading to populate current weight
      setTimeout(() => {
        this.requestWeightReading();
      }, 500);
    },

    onScaleDisconnected(scaleId) {
      const scale = this.scalesMap.get(scaleId);
      if (scale) {
        const updatedScale = { ...scale, isConnected: false };
        this.scalesMap.set(scaleId, updatedScale);
        // Update the array
        const index = this.scales.findIndex(s => s.id === scaleId);
        if (index >= 0) {
          this.$set(this.scales, index, updatedScale);
        }
      }
    },

    onWeightUpdate(data) {
      this.currentWeight = data.weight;
      // Weight readings are now only displayed within the widget, no activity notifications

      // Log weight reading if Test Scale dialog is open
      if (this.showTestScaleDialog && data.weight) {
        this.logWeightReading(data.weight, 'Live reading');
      }
    },

    onPrinterConnected(printer) {
      console.log('[HardwareStatus] Printer connected:', printer);
      const updatedPrinter = { ...printer, isConnected: true };
      this.printersMap.set(printer.id, updatedPrinter);
      // Update the array
      const index = this.printers.findIndex(p => p.id === printer.id);
      if (index >= 0) {
        this.$set(this.printers, index, updatedPrinter);
      } else {
        // If printer not in array, add it
        this.printers.push(updatedPrinter);
      }
      this.showRecentActivity('printer', 'mdi-printer', `Printer ${printer.name} connected`);
    },

    onPrinterDisconnected(printerId) {
      const printer = this.printersMap.get(printerId);
      if (printer) {
        const updatedPrinter = { ...printer, isConnected: false };
        this.printersMap.set(printerId, updatedPrinter);
        // Update the array
        const index = this.printers.findIndex(p => p.id === printerId);
        if (index >= 0) {
          this.$set(this.printers, index, updatedPrinter);
        }
      }
    },

    onPrintCompleted(data) {
      this.showRecentActivity('print', 'mdi-printer-check', 'Print completed');
    },

    onScannerConnected(scanner) {
      console.log('[HardwareStatus] Scanner connected:', scanner);
      const updatedScanner = { ...scanner, isConnected: true };
      this.scannersMap.set(scanner.id, updatedScanner);
      // Update the array
      const index = this.scanners.findIndex(s => s.id === scanner.id);
      if (index >= 0) {
        this.$set(this.scanners, index, updatedScanner);
      } else {
        // If scanner not in array, add it
        this.scanners.push(updatedScanner);
      }
      this.showRecentActivity('scanner', 'mdi-barcode-scan', `Scanner ${scanner.name} connected`);
    },

    onScannerDisconnected(scannerId) {
      const scanner = this.scannersMap.get(scannerId);
      if (scanner) {
        const updatedScanner = { ...scanner, isConnected: false };
        this.scannersMap.set(scannerId, updatedScanner);
        // Update the array
        const index = this.scanners.findIndex(s => s.id === scannerId);
        if (index >= 0) {
          this.$set(this.scanners, index, updatedScanner);
        }
      }
    },

    onPickTicketScanned(data) {
      this.showRecentActivity('scan', 'mdi-check', `PT #${data.pickTicketId}`);
    },

    onBarcodeScanned(data) {
      const preview = data.data && data.data.length > 15
        ? data.data.substring(0, 12) + '...'
        : data.data;
      this.showRecentActivity('scan', 'mdi-barcode-scan', preview);
    },

    showRecentActivity(type, icon, text) {
      this.recentActivity = { type, icon, text };

      if (this.activityTimeout) {
        clearTimeout(this.activityTimeout);
      }

      this.activityTimeout = setTimeout(() => {
        this.recentActivity = null;
        this.activityTimeout = null;
      }, 3000);
    },

    // Device Actions
    async connectFirstAvailableScale() {
      const firstScale = this.scales[0];
      if (firstScale) {
        try {
          await this.hardwareService.connectToScale(firstScale.id);
        } catch (error) {
          console.error('[HardwareStatus] Failed to connect to scale:', error);
          this.$toast?.error('Failed to connect to scale');
        }
      }
    },

    async connectFirstAvailablePrinter() {
      const firstPrinter = this.printers[0];
      if (firstPrinter) {
        try {
          await this.hardwareService.connectToPrinter(firstPrinter.id);
        } catch (error) {
          console.error('[HardwareStatus] Failed to connect to printer:', error);
          this.$toast?.error('Failed to connect to printer');
        }
      }
    },

    async connectFirstAvailableScanner() {
      const firstScanner = this.scanners[0];
      if (firstScanner) {
        try {
          await this.hardwareService.connectToScanner(firstScanner.id);
        } catch (error) {
          console.error('[HardwareStatus] Failed to connect to scanner:', error);
          this.$toast?.error('Failed to connect to scanner');
        }
      }
    },

    async requestWeightReading() {
      const connectedScale = this.scales.find(s => s.isConnected);
      if (connectedScale && !this.isWaitingForWeight) {
        this.isWaitingForWeight = true;
        try {
          const weight = await this.hardwareService.getWeight(connectedScale.id);
          this.currentWeight = weight;
          this.showRecentActivity('weight', 'mdi-weight', `${weight.value} ${weight.unit}`);
        } catch (error) {
          console.error('[HardwareStatus] Failed to get weight:', error);
        } finally {
          this.isWaitingForWeight = false;
        }
      }
    },

    // New Scale Methods
    async zeroScale() {
      const connectedScale = this.scales.find(s => s.isConnected);
      if (!connectedScale) {
        this.$toast?.error('No scale connected');
        return;
      }

      this.isZeroingScale = true;
      try {
        // Use the hardware service to zero the scale
        // Note: This assumes the hardware service has a zero method
        // You may need to add this method to the hardware service
        if (this.hardwareService && this.hardwareService.zeroScale) {
          await this.hardwareService.zeroScale(connectedScale.id);
        } else {
          // Fallback: just log a zero weight entry
          this.logWeightReading(0, 'Scale zeroed');
        }

        this.logWeightReading(0, 'Scale zeroed');
        this.$toast?.success('Scale zeroed successfully');
        this.showRecentActivity('scale', 'mdi-refresh', 'Scale zeroed');

        // Update current weight to 0
        this.currentWeight = { value: 0, unit: 'lbs', isStable: true };
      } catch (error) {
        console.error('[HardwareStatus] Failed to zero scale:', error);
        this.$toast?.error('Failed to zero scale');
      } finally {
        this.isZeroingScale = false;
      }
    },

    logWeightReading(weight, action = 'Test Scale reading') {
      const entry = {
        weight: typeof weight === 'object' ? `${weight.value} ${weight.unit}` : `${weight} lbs`,
        action,
        timestamp: new Date().toISOString(),
        displayTime: new Date().toLocaleTimeString()
      };

      this.weightHistory.unshift(entry);

      // Keep only the most recent entries
      if (this.weightHistory.length > this.maxLogEntries) {
        this.weightHistory = this.weightHistory.slice(0, this.maxLogEntries);
      }
    },

    clearWeightLog() {
      this.weightHistory = [];
      this.$toast?.success('Weight log cleared');
    },

    async refreshDevices() {
      await this.discoverAllDevices();
      this.$toast?.info('Device list refreshed');
    },

    startAutoRefresh() {
      if (this.autoRefreshTimer) {
        return; // Already running
      }

      console.log('[HardwareStatus] Starting auto-refresh with interval:', this.autoRefreshInterval);
      this.autoRefreshTimer = setInterval(async () => {
        if (this.uhbAvailable && this.isVisible) {
          try {
            console.log('[HardwareStatus] Auto-refreshing device status...');
            const now = Date.now();

            // Request weight readings from connected scales, but only if we're not already waiting for one
            const connectedScale = this.scales.find(s => s.isConnected);
            if (connectedScale && !this.isWaitingForWeight) {
              this.isWaitingForWeight = true;
              try {
                await this.hardwareService.getWeight(connectedScale.id);
              } catch (error) {
                // Silently handle weight reading errors
                console.debug('[HardwareStatus] Weight reading failed during auto-refresh:', error);
              } finally {
                this.isWaitingForWeight = false;
              }
            }

            // Determine if we need a full refresh based on current state
            const hasNoDevices = this.scales.length === 0 && this.printers.length === 0 && this.scanners.length === 0;
            const timeSinceLastRefresh = now - this.lastFullRefresh;

            // Use quick refresh interval if we have no devices (still discovering) or if we're in "Hardware Available" state
            // Use normal interval if we already have devices and they're working
            const isInDiscoveryMode = hasNoDevices || (this.hasDevices && !this.hasConnectedDevices);
            const refreshInterval = isInDiscoveryMode ? this.quickRefreshInterval : this.fullRefreshInterval;

            const needsFullRefresh = hasNoDevices || (timeSinceLastRefresh > refreshInterval);

            if (needsFullRefresh) {
              console.log('[HardwareStatus] Performing full device refresh...', {
                hasNoDevices,
                isInDiscoveryMode,
                timeSinceLastRefresh,
                refreshInterval,
                hasDevices: this.hasDevices,
                hasConnectedDevices: this.hasConnectedDevices
              });
              await this.discoverAllDevices();
              this.lastFullRefresh = now;
            } else {
              // Even when not doing full refresh, verify connection states periodically
              await this.verifyAllConnectionStates();
            }
          } catch (error) {
            console.error('[HardwareStatus] Auto-refresh failed:', error);
            // Don't show toast errors for auto-refresh failures
          }
        }
      }, this.autoRefreshInterval);
    },

    stopAutoRefresh() {
      if (this.autoRefreshTimer) {
        console.log('[HardwareStatus] Stopping auto-refresh');
        clearInterval(this.autoRefreshTimer);
        this.autoRefreshTimer = null;
      }
    },

    submitTestBarcode() {
      const barcode = this.testBarcode.trim();
      if (!barcode) {
        this.$toast?.warning('Please enter a barcode value');
        return;
      }

      this.showScanTestDialog = false;

      // Try keyboard wedge scanner first
      const wedgeScanner = getKeyboardWedgeScanner();
      if (wedgeScanner) {
        wedgeScanner.testScan(barcode);
      } else if (this.scannerService) {
        const handleScan = this.scannerService['handleScan'];
        if (handleScan) {
          handleScan.call(this.scannerService, barcode);
        }
      }

      this.showRecentActivity('scan', 'mdi-keyboard', barcode);
      this.testBarcode = '';
    },

    async submitTestPrint() {
      if (!this.selectedPrinter) {
        this.$toast?.warning('Please select a printer');
        return;
      }

      // Prevent multiple simultaneous print requests
      if (this.isPrinting) {
        console.warn('[HardwareStatus] Print already in progress, ignoring request');
        return;
      }
      this.isPrinting = true;

      try {
        // Add timeout safeguard
        const printPromise = this.executePrintJob();
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Print timeout after 10 seconds')), 10000);
        });

        await Promise.race([printPromise, timeoutPromise]);

        this.$toast?.success('Test print sent');
        this.showPrintTestDialog = false;
      } catch (error) {
        console.error('[HardwareStatus] Test print failed:', error);
        this.$toast?.error('Test print failed');
      } finally {
        // Always reset the printing flag
        this.isPrinting = false;
      }
    },

    async executePrintJob() {
      console.log('[HardwareStatus] Starting PDF-based test print');

      try {
        // Get printer name for display
        const printerName = this.availablePrinters.find(p => p.id === this.selectedPrinter)?.name || this.selectedPrinter;

        // Detect printer type
        const printerType = TestPrintService.detectPrinterType(printerName);
        console.log(`[HardwareStatus] Detected printer type: ${printerType} for "${printerName}"`);

        // Generate test PDF based on printer type
        const testPdfDataUrl = TestPrintService.generateTestPdf({
          printerName: printerName,
          printerType: printerType,
          includeBarcode: true,
          includeGraphics: true
        });

        console.log(`[HardwareStatus] Generated test PDF data URL (${testPdfDataUrl.length} characters)`);

        // Use the existing printLabelFromUrl method which now supports data URLs
        const result = await this.hardwareService.printLabelFromUrl(this.selectedPrinter, testPdfDataUrl);

        if (!result.success) {
          throw new Error(result.error || 'Print job failed');
        }

        console.log('[HardwareStatus] PDF test print completed successfully');
        return result;

      } catch (error) {
        console.error('[HardwareStatus] PDF test print failed:', error);

        // Fallback to simple test if comprehensive test fails
        console.log('[HardwareStatus] Attempting fallback simple test print');
        try {
          const printerName = this.availablePrinters.find(p => p.id === this.selectedPrinter)?.name || this.selectedPrinter;
          const simpleTestPdfDataUrl = TestPrintService.generateSimpleTest(printerName);

          const fallbackResult = await this.hardwareService.printLabelFromUrl(this.selectedPrinter, simpleTestPdfDataUrl);

          if (fallbackResult.success) {
            console.log('[HardwareStatus] Fallback simple test print succeeded');
            return fallbackResult;
          }
        } catch (fallbackError) {
          console.error('[HardwareStatus] Fallback test also failed:', fallbackError);
        }

        // If both PDF methods fail, rethrow the original error
        throw error;
      }
    },

    // Load printer settings from shared configuration service
    loadPrinterSettings() {
      if (!this.printerConfigService) {
        this.printerConfigService = getPrinterConfiguration();
      }

      const config = this.printerConfigService.getConfiguration();

      this.autoPrintEnabled = config.autoPrintLabels;
      this.selectedAutoPrintPrinter = config.labelPrinterId;
      this.selectedLabelLayout = config.defaultLabelLayout || '4x6';

      // If no printer is selected but auto-print is enabled, disable auto-print
      if (this.autoPrintEnabled && !this.selectedAutoPrintPrinter) {
        this.autoPrintEnabled = false;
      }

      // Validate that the selected printer still exists
      if (this.selectedAutoPrintPrinter) {
        const printerExists = this.availablePrinters.some(p => p.id === this.selectedAutoPrintPrinter);
        if (!printerExists) {
          // Don't clear the selection yet - printer might not be discovered yet
          // It will be validated again when printers are discovered
          console.log('[HardwareStatus] Selected printer not yet available:', this.selectedAutoPrintPrinter);
        }
      }
    },

    // Save printer settings to shared configuration service
    savePrinterSettings() {
      if (!this.printerConfigService) {
        this.printerConfigService = getPrinterConfiguration();
      }

      // Update the shared configuration
      this.printerConfigService.updateConfiguration({
        labelPrinterId: this.selectedAutoPrintPrinter,
        autoPrintLabels: this.autoPrintEnabled,
        defaultLabelLayout: this.selectedLabelLayout,
        // Also set the pick ticket printer to the same printer if not already set
        pickTicketPrinterId: this.printerConfigService.getPickTicketPrinter() || this.selectedAutoPrintPrinter
      });

      this.$toast?.success('Printer settings saved');
      this.showPrinterSettingsDialog = false;

      // The PrinterConfigurationService will automatically emit events
      // But we can also emit a specific event for backwards compatibility
      this.$bus?.$emit('printer-settings-updated', {
        autoPrintEnabled: this.autoPrintEnabled,
        selectedPrinterId: this.selectedAutoPrintPrinter
      });
    },

    openDownloadLink() {
      // Open UHB download page - this would be the actual download URL
      const downloadUrl = 'https://tloesstoragedev.blob.core.windows.net/uhb-releases/latest/windows/Universal-Hardware-Bridge-Setup.exe';
      window.open(downloadUrl, '_blank');
    },

    hideWidget() {
      this.isVisible = false;
      this.widgetVisible = false;
      this.saveVisibilitySetting();

      // Emit event to notify Navigation component
      this.$root.$emit('hardware-widget-toggle', false);

      // Show one-time session guidance toast
      const hasShownGuidance = sessionStorage.getItem('hardware_widget_close_guidance_shown');
      if (!hasShownGuidance) {
        this.$toast?.info('Hardware widget hidden. You can re-enable it anytime from your user menu (click your initials in the top right → Hardware Widget).', {
          timeout: 8000
        });
        sessionStorage.setItem('hardware_widget_close_guidance_shown', 'true');

        // Emit event to highlight user navigation with toast duration
        this.$root.$emit('highlight-user-navigation', 8000);
      }
      // No toast needed for subsequent closes - user can see the visual change
    },

    toggleCollapse() {
      console.log('[HardwareStatus] Toggle collapse clicked, current state:', this.collapsed);
      this.collapsed = !this.collapsed;
      console.log('[HardwareStatus] New collapsed state:', this.collapsed);

      // Save collapsed state to localStorage
      localStorage.setItem('hardware_widget_collapsed', this.collapsed.toString());
    },

    loadVisibilitySetting() {
      const saved = localStorage.getItem('hardware_widget_visible');
      this.isVisible = saved !== 'false';
      this.widgetVisible = this.isVisible;

      // Load collapsed state
      const collapsedSaved = localStorage.getItem('hardware_widget_collapsed');
      this.collapsed = collapsedSaved === 'true';
      console.log('[HardwareStatus] Loaded collapsed state:', this.collapsed);
    },

    saveVisibilitySetting() {
      localStorage.setItem('hardware_widget_visible', this.widgetVisible.toString());
      this.isVisible = this.widgetVisible;

      // Manage auto-refresh based on visibility
      if (this.isVisible && this.uhbAvailable && !this.autoRefreshTimer) {
        this.startAutoRefresh();
      } else if (!this.isVisible && this.autoRefreshTimer) {
        this.stopAutoRefresh();
      }
    },

    // Category expansion methods
    toggleCategoryExpansion(categoryName) {
      this.expandedCategories[categoryName] = !this.expandedCategories[categoryName];
    },

    // Device list toggle
    toggleDeviceList(categoryName) {
      this.showDeviceList[categoryName] = !this.showDeviceList[categoryName];
      // Close actions panel when showing device list
      if (this.showDeviceList[categoryName]) {
        this.expandedCategories[categoryName] = false;
      }
    },

    // Auto-connect functionality
    async autoConnectDevices() {
      if (!this.hardwareService?.isConnected) {
        console.log('[HardwareStatus] Skipping auto-connect: UHB not available');
        return;
      }

      console.log('[HardwareStatus] Starting auto-connect for available devices...');

      // Check if auto-connect is enabled (user preference)
      const autoConnectEnabled = localStorage.getItem('hardware_auto_connect') !== 'false';
      if (!autoConnectEnabled) {
        console.log('[HardwareStatus] Auto-connect disabled by user preference');
        return;
      }

      const connectionPromises = [];

      // Auto-connect first available scale
      if (this.scales.length > 0 && this.connectedScales === 0) {
        const firstScale = this.scales.find(s => !s.isConnected) || this.scales[0];
        if (firstScale && !firstScale.isConnected) {
          connectionPromises.push(
            this.connectToDeviceWithRetry(firstScale, 'scale')
              .then(() => console.log('[HardwareStatus] Auto-connected to scale:', firstScale.id))
              .catch(error => console.warn('[HardwareStatus] Auto-connect to scale failed:', error))
          );
        }
      }

      // Auto-connect first available printer
      if (this.printers.length > 0 && this.connectedPrinters === 0) {
        const firstPrinter = this.printers.find(p => !p.isConnected) || this.printers[0];
        if (firstPrinter && !firstPrinter.isConnected) {
          connectionPromises.push(
            this.connectToDeviceWithRetry(firstPrinter, 'printer')
              .then(() => console.log('[HardwareStatus] Auto-connected to printer:', firstPrinter.id))
              .catch(error => console.warn('[HardwareStatus] Auto-connect to printer failed:', error))
          );
        }
      }

      // Auto-connect first available scanner
      if (this.scanners.length > 0 && this.connectedScanners === 0) {
        const firstScanner = this.scanners.find(s => !s.isConnected) || this.scanners[0];
        if (firstScanner && !firstScanner.isConnected) {
          connectionPromises.push(
            this.connectToDeviceWithRetry(firstScanner, 'scanner')
              .then(() => console.log('[HardwareStatus] Auto-connected to scanner:', firstScanner.id))
              .catch(error => console.warn('[HardwareStatus] Auto-connect to scanner failed:', error))
          );
        }
      }

      // Wait for all connections to complete (with timeout)
      if (connectionPromises.length > 0) {
        try {
          await Promise.allSettled(connectionPromises);
          console.log('[HardwareStatus] Auto-connect phase completed');
        } catch (error) {
          console.error('[HardwareStatus] Auto-connect error:', error);
        }
      }
    },

    // Helper method for connecting with retry logic
    async connectToDeviceWithRetry(device, deviceType, maxRetries = 2) {
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          switch (deviceType) {
            case 'scale':
              await this.hardwareService.connectToScale(device.id);
              break;
            case 'printer':
              await this.hardwareService.connectToPrinter(device.id);
              break;
            case 'scanner':
              await this.hardwareService.connectToScanner(device.id);
              break;
          }
          return; // Success, exit retry loop
        } catch (error) {
          console.warn(`[HardwareStatus] Connection attempt ${attempt}/${maxRetries} failed for ${deviceType} ${device.id}:`, error);
          if (attempt === maxRetries) {
            throw error; // Last attempt failed, rethrow
          }
          // Wait before retry
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        }
      }
    },

    // Manual device connection with state sync
    async connectToDevice(device, deviceType) {
      if (this.isDeviceLoading(device.id)) {
        console.log('[HardwareStatus] Device already loading:', device.id);
        return; // Already loading
      }

      try {
        // Set loading state
        console.log('[HardwareStatus] Setting loading state for connect:', device.id);
        this.setDeviceLoading(device.id, 'connecting');
        console.log('[HardwareStatus] Loading states after set:', this.deviceOperationLoading);

        // Optimistically update UI state
        this.updateDeviceConnectionState(device.id, deviceType, true);

        switch (deviceType) {
          case 'scale':
            await this.hardwareService.connectToScale(device.id);
            this.$toast?.success(`Connected to scale: ${device.name || device.id}`);
            break;
          case 'printer':
            await this.hardwareService.connectToPrinter(device.id);
            this.$toast?.success(`Connected to printer: ${device.name || device.id}`);
            break;
          case 'scanner':
            await this.hardwareService.connectToScanner(device.id);
            this.$toast?.success(`Connected to scanner: ${device.name || device.id}`);
            break;
        }

        // Verify connection state after successful connection
        setTimeout(() => {
          this.verifyDeviceConnectionState(device.id, deviceType);
        }, 1000);

      } catch (error) {
        console.error(`[HardwareStatus] Failed to connect to ${deviceType}:`, error);
        // Revert optimistic UI update on failure
        this.updateDeviceConnectionState(device.id, deviceType, false);
        this.$toast?.error(`Failed to connect to ${deviceType}`);
      } finally {
        // Clear loading state
        console.log('[HardwareStatus] Clearing loading state for connect:', device.id);
        this.clearDeviceLoading(device.id);
        console.log('[HardwareStatus] Loading states after clear:', this.deviceOperationLoading);
      }
    },

    // Manual device disconnection with state sync
    async disconnectFromDevice(device, deviceType) {
      if (this.isDeviceLoading(device.id)) {
        console.log('[HardwareStatus] Device already loading for disconnect:', device.id);
        return; // Already loading
      }

      try {
        // Set loading state
        console.log('[HardwareStatus] Setting loading state for disconnect:', device.id);
        this.setDeviceLoading(device.id, 'disconnecting');
        console.log('[HardwareStatus] Loading states after set:', this.deviceOperationLoading);

        // Optimistically update UI state
        this.updateDeviceConnectionState(device.id, deviceType, false);

        switch (deviceType) {
          case 'scale':
            await this.hardwareService.disconnectFromScale?.(device.id);
            this.$toast?.info(`Disconnected from scale: ${device.name || device.id}`);
            break;
          case 'printer':
            await this.hardwareService.disconnectFromPrinter?.(device.id);
            this.$toast?.info(`Disconnected from printer: ${device.name || device.id}`);
            break;
          case 'scanner':
            await this.hardwareService.disconnectFromScanner?.(device.id);
            this.$toast?.info(`Disconnected from scanner: ${device.name || device.id}`);
            break;
        }

        // Verify disconnection state
        setTimeout(() => {
          this.verifyDeviceConnectionState(device.id, deviceType);
        }, 1000);

      } catch (error) {
        console.error(`[HardwareStatus] Failed to disconnect from ${deviceType}:`, error);
        // Revert optimistic UI update on failure
        this.updateDeviceConnectionState(device.id, deviceType, true);
        this.$toast?.error(`Failed to disconnect from ${deviceType}`);
      } finally {
        // Clear loading state
        console.log('[HardwareStatus] Clearing loading state for disconnect:', device.id);
        this.clearDeviceLoading(device.id);
        console.log('[HardwareStatus] Loading states after clear:', this.deviceOperationLoading);
      }
    },

    // Helper methods for connection state management
    updateDeviceConnectionState(deviceId, deviceType, isConnected) {
      let deviceArray, deviceMap;

      switch (deviceType) {
        case 'scale':
          deviceArray = this.scales;
          deviceMap = this.scalesMap;
          break;
        case 'printer':
          deviceArray = this.printers;
          deviceMap = this.printersMap;
          break;
        case 'scanner':
          deviceArray = this.scanners;
          deviceMap = this.scannersMap;
          break;
        default:
          return;
      }

      // Update in map
      const device = deviceMap.get(deviceId);
      if (device) {
        const updatedDevice = { ...device, isConnected };
        deviceMap.set(deviceId, updatedDevice);

        // Update in array
        const index = deviceArray.findIndex(d => d.id === deviceId);
        if (index >= 0) {
          this.$set(deviceArray, index, updatedDevice);
        }
      }
    },

    async verifyDeviceConnectionState(deviceId, deviceType) {
      // Skip verification in UHB mode since connection states are managed by WebSocket events
      // The UHB service doesn't expose isXxxConnected() methods and connection states
      // are accurately maintained through device.connected/device.disconnected events
      // This component always uses UHB mode (getHardwareService(true))
      return;
    },

    getCurrentDeviceConnectionState(deviceId, deviceType) {
      switch (deviceType) {
        case 'scale':
          return this.scalesMap.get(deviceId)?.isConnected || false;
        case 'printer':
          return this.printersMap.get(deviceId)?.isConnected || false;
        case 'scanner':
          return this.scannersMap.get(deviceId)?.isConnected || false;
        default:
          return false;
      }
    },

    // Helper method to format weight values
    formatWeight(weight) {
      if (typeof weight !== 'number') {
        return weight;
      }

      // Format to 2 decimal places and remove trailing zeros
      const formatted = parseFloat(weight.toFixed(2));
      return formatted.toString();
    },

    // Loading state management
    setDeviceLoading(deviceId, operation) {
      this.$set(this.deviceOperationLoading, deviceId, operation);
    },

    clearDeviceLoading(deviceId) {
      this.$delete(this.deviceOperationLoading, deviceId);
    },

    isDeviceLoading(deviceId) {
      return deviceId in this.deviceOperationLoading;
    },

    getDeviceLoadingOperation(deviceId) {
      return this.deviceOperationLoading[deviceId];
    },

    // Periodic connection state verification
    async verifyAllConnectionStates() {
      if (!this.hardwareService?.isConnected) return;

      const verificationPromises = [];

      // Verify all scale connections
      this.scales.forEach(scale => {
        verificationPromises.push(this.verifyDeviceConnectionState(scale.id, 'scale'));
      });

      // Verify all printer connections
      this.printers.forEach(printer => {
        verificationPromises.push(this.verifyDeviceConnectionState(printer.id, 'printer'));
      });

      // Verify all scanner connections
      this.scanners.forEach(scanner => {
        verificationPromises.push(this.verifyDeviceConnectionState(scanner.id, 'scanner'));
      });

      if (verificationPromises.length > 0) {
        try {
          await Promise.allSettled(verificationPromises);
        } catch (error) {
          console.warn('[HardwareStatus] Connection state verification failed:', error);
        }
      }
    },

    // Scale-specific actions
    async handleScaleAction(action) {
      switch (action) {
        case 'testScale':
          this.showTestScaleDialog = true;
          break;
        case 'zeroScale':
          await this.zeroScale();
          break;
        case 'weightLog':
          this.showWeightLogDialog = true;
          break;
      }
      this.expandedCategories.scale = false;
    },

    // Printer-specific actions
    async handlePrinterAction(action) {
      switch (action) {
        case 'testPrint':
          this.showPrintTestDialog = true;
          break;
        case 'settings':
          this.loadPrinterSettings();
          this.showPrinterSettingsDialog = true;
          break;
      }
      this.expandedCategories.printer = false;
    },

    // Scanner-specific actions
    async handleScannerAction(action) {
      switch (action) {
        case 'testScan':
          this.showScanTestDialog = true;
          break;
      }
      this.expandedCategories.scanner = false;
    },

    // Enhanced test scan functionality
    async submitTestScan() {
      if (!this.testScanValue.trim()) {
        this.$toast?.error('Please enter a barcode value');
        return;
      }

      const barcode = this.testScanValue.trim();
      console.log('[HardwareStatus] Manual test barcode:', barcode);

      // Close dialog first
      this.showScanTestDialog = false;
      this.testScanValue = '';

      try {
        // Try keyboard wedge scanner first (most likely to work and trigger full pipeline)
        const wedgeScanner = getKeyboardWedgeScanner();
        if (wedgeScanner) {
          console.log('[HardwareStatus] Using keyboard wedge scanner for test');
          wedgeScanner.testScan(barcode);
        } else if (this.scannerService) {
          // Fallback to scanner service
          console.log('[HardwareStatus] Using scanner service for test');
          // Call handleScan directly (using private method via reflection)
          const handleScan = this.scannerService['handleScan'];
          if (handleScan) {
            handleScan.call(this.scannerService, barcode);
          } else {
            this.$toast?.warning('Scanner service handleScan method not available');
            return;
          }
        } else {
          this.$toast?.warning('No scanner service available for test');
          return;
        }

        // The actual processing will happen in the scanner service
        // and will show appropriate toasts/dialogs based on the barcode type

      } catch (error) {
        console.error('[HardwareStatus] Test scan failed:', error);
        this.$toast?.error('Test scan failed');
      }
    },

    cleanup() {
      // Stop auto-refresh
      this.stopAutoRefresh();

      if (this.activityTimeout) {
        clearTimeout(this.activityTimeout);
        this.activityTimeout = null;
      }

      if (this.hardwareService) {
        this.hardwareService.removeListener('connected', this.onUHBConnected);
        this.hardwareService.removeListener('disconnected', this.onUHBDisconnected);
        this.hardwareService.removeListener('devices-updated', this.onDevicesUpdated);
        this.hardwareService.removeListener('scale-connected', this.onScaleConnected);
        this.hardwareService.removeListener('scale-disconnected', this.onScaleDisconnected);
        this.hardwareService.removeListener('weight', this.onWeightUpdate);
        this.hardwareService.removeListener('printer-connected', this.onPrinterConnected);
        this.hardwareService.removeListener('printer-disconnected', this.onPrinterDisconnected);
        this.hardwareService.removeListener('print-completed', this.onPrintCompleted);
        this.hardwareService.removeListener('scanner-connected', this.onScannerConnected);
        this.hardwareService.removeListener('scanner-disconnected', this.onScannerDisconnected);
      }

      if (this.scannerService) {
        this.scannerService.removeListener('pick-ticket-scanned', this.onPickTicketScanned);
        this.scannerService.removeListener('barcode-scanned', this.onBarcodeScanned);
      }

      this.showStatus = false;
      this.scales.splice(0, this.scales.length);
      this.printers.splice(0, this.printers.length);
      this.scanners.splice(0, this.scanners.length);
      this.scalesMap.clear();
      this.printersMap.clear();
      this.scannersMap.clear();
    }
  }
};
</script>

<style lang="scss" scoped>
.hardware-status {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: white;  
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  z-index: 1000;
  transition: all 0.2s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

  // Clean state colors
  &.connected {
    border-left: 3px solid #4caf50;
  }

  &.warning {
    border-left: 3px solid #ff9800;
  }

  &.disconnected,
  &.no-devices {
    border-left: 3px solid #757575;
  }

  &.initializing {
    border-left: 3px solid #e8e8e8;
    background: #fafafa;
  }
}

// UHB Warning - Clean & Minimal
.uhb-warning {
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff3cd;
  border-radius: 4px;
  min-width: 280px;

  .warning-icon {
    color: #856404;
  }

  .warning-info {
    flex: 1;
  }

  .warning-title {
    font-size: 13px;
    font-weight: 500;
    color: #856404;
    margin-bottom: 4px;
  }

  .download-link {
    font-size: 12px;
    text-transform: none;
    padding: 0;
    min-width: auto;
    height: auto;
  }

  .close-button {
    color: #856404;
    margin-left: auto;

    &:hover {
      background: rgba(133, 100, 4, 0.1);
    }
  }
}

// Main Status Content
.status-content {
  padding: 10px 14px;
  min-width: 200px;
  max-width: 300px;
}

.status-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;

  .connected & {
    color: #4caf50;
    animation: pulse 2s infinite;
  }

  .warning & {
    color: #ff9800;
  }

  .disconnected &,
  .no-devices & {
    color: #757575;
  }

  .initializing & {
    color: #9e9e9e;
    animation: none;
  }
}

.status-label {
  font-size: 13px;
  font-weight: 500;
  color: #333;
}

.device-summary {
  display: flex;
  gap: 4px;
  margin-left: 8px;
}

.device-badge {
  display: flex;
  align-items: center;
  gap: 3px;
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 500;
  color: #666;

  &.scale-badge {
    background: #e8f5e8;
    color: #4caf50;
  }

  &.printer-badge {
    background: #e3f2fd;
    color: #2196f3;
  }

  &.scanner-badge {
    background: #fff3e0;
    color: #ff9800;
  }
}

.status-actions {
  display: flex;
  gap: 2px;
  margin-left: 8px;
}

.action-btn {
  opacity: 0.6;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }

  &.hide-btn:hover {
    color: #f44336 !important;
  }
}

// Weight Display
.weight-display {
  background: #f0f7ff;
  border-radius: 4px;
  padding: 6px 10px;
  margin-bottom: 8px;
  text-align: center;
  border: 1px solid #e3f2fd;

  .weight-value {
    font-size: 16px;
    font-weight: 600;
    color: #2196f3;
  }

  .weight-status {
    font-size: 11px;
    color: #757575;
    font-style: italic;
    margin-left: 8px;
  }
}

// Device List
.device-list {
  max-height: 120px;
  overflow-y: auto;
}

.device-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  font-size: 12px;

  &:not(:last-child) {
    border-bottom: 1px solid #f5f5f5;
    padding-bottom: 6px;
    margin-bottom: 2px;
  }
}

.device-name {
  flex: 1;
  color: #333;
  font-weight: 500;
}

.device-status {
  font-size: 9px !important;
  height: 18px !important;
}

// Recent Activity
.recent-activity {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: #f1f8e9;
  border-radius: 3px;
  margin-top: 6px;

  .activity-text {
    font-size: 11px;
    color: #4caf50;
    font-weight: 500;
  }
}

// Minimized State
.hardware-status.minimized {
  .status-content {
    padding: 8px 12px;
  }

  .weight-display,
  .device-list,
  .recent-activity {
    display: none;
  }
}

// Animations
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.activity-flash-enter-active {
  animation: fadeInScale 0.3s ease;
}

.activity-flash-leave-active {
  animation: fadeOutScale 0.2s ease;
}

@keyframes fadeInScale {
  0% {
    opacity: 0;
    transform: scale(0.9);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes fadeOutScale {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(0.95);
  }
}

// New Expandable Categories Design
.hardware-status-new {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  min-width: 320px;
  max-width: 400px;
  max-height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  // Status-based styling
  &.connected {
    border-left: 4px solid #4caf50;
    .status-dot.status-hardware-ready {
      color: #4caf50;
      animation: pulse 2s infinite;
    }
  }

  &.warning {
    border-left: 4px solid #ff9800;
    .status-dot.status-hardware-available {
      color: #ff9800;
      animation: pulse 2s infinite;
    }
  }

  &.disconnected, &.no-devices {
    border-left: 4px solid #757575;
  }

  &.initializing {
    border-left: 4px solid #2196f3;
    .status-dot.status-initializing {
      color: #2196f3;
      animation: pulse 1.5s infinite;
    }
  }

  // Collapsed state - similar to scanner status dot
  &.collapsed {
    min-width: 50px;
    max-width: 50px;

    .categories-header {
      padding: 12px;
      border-bottom: none;

      .status-indicator {
        justify-content: center;

        .status-label {
          display: none;
        }

        .status-dot {
          width: 12px;
          height: 12px;
        }
      }

      .header-actions {
        display: none;
      }
    }
  }
}

.categories-container {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

// Header Section
.categories-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
  border-bottom: 1px solid #f0f0f0;
  background: linear-gradient(135deg, #fafafa 0%, #ffffff 100%);
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  .status-indicator {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;

    .status-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex-shrink: 0;
      background: currentColor;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .status-label {
      font-size: 14px;
      font-weight: 600;
      color: #333;
      letter-spacing: 0.3px;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
  }

  .header-actions {
    display: flex;
    gap: 4px;

    .action-btn {
      opacity: 0.7;
      transition: opacity 0.2s ease;

      &:hover {
        opacity: 1;
      }
    }
  }

  &.clickable {
    cursor: pointer;

    &:hover {
      background: rgba(0, 0, 0, 0.02);
    }
  }
}

// Categories List
.categories-list {
  padding: 8px 0;
  overflow-y: auto;
  min-height: 0;
}

.category-item {
  transition: all 0.3s ease;

  &:hover {
    background: #f8f9fa;
  }

  &.expanded {
    background: #f8f9fa;
  }
}

.category-row {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 56px;

  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
}

.category-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.04);
  margin-right: 12px;
  transition: all 0.2s ease;

  .category-item:hover & {
    background: rgba(0, 0, 0, 0.08);
  }

  .v-icon {
    font-size: 18px !important;
  }
}

.category-info {
  flex: 1;
  min-width: 0;

  .category-name {
    font-size: 15px;
    font-weight: 500;
    color: #333;
    margin-bottom: 2px;
  }

  .category-details {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;

    .category-status {
      font-size: 12px;
      font-weight: 500;
      padding: 2px 6px;
      border-radius: 4px;
      text-transform: uppercase;
      letter-spacing: 0.3px;

      &.status-success {
        background: #e8f5e8;
        color: #2e7d32;
      }

      &.status-warning {
        background: #fff3e0;
        color: #f57c00;
      }

      &.status-grey {
        background: #f5f5f5;
        color: #757575;
      }
    }

    .category-primary {
      font-size: 13px;
      color: #555;
      font-weight: 500;
    }

    .category-secondary {
      font-size: 11px;
      color: #888;
      font-style: italic;
    }
  }
}

.category-actions {
  margin-left: 8px;
}

// Device List Panel
.device-list-panel {
  padding: 12px 20px 16px 68px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
  overflow: hidden;

  .device-list-header {
    margin-bottom: 12px;

    .device-count {
      font-size: 12px;
      color: #666;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }

  .device-list-item {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 12px 0;
    border-bottom: 1px solid #e8e8e8;
    gap: 12px;

    &:last-child {
      border-bottom: none;
    }

    &.connected {
      .device-name {
        color: #2e7d32;
        font-weight: 500;
      }
    }

    .device-info {
      flex: 1;
      min-width: 0;

      .device-name {
        font-size: 13px;
        font-weight: 500;
        color: #333;
        margin-bottom: 6px;
        line-height: 1.3;
      }

      .device-details {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
        min-width: 0;

        .device-id {
          font-size: 11px;
          color: #888;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 100%;
        }
      }
    }

    .device-actions {
      flex-shrink: 0;
      display: flex;
      align-items: flex-start;
      margin-top: 2px;
    }
  }
}

// Expandable Actions Panel
.actions-panel {
  padding: 16px 20px 20px 68px;
  border-top: 1px solid #f0f0f0;
  background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%);
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  .action-button {
    font-size: 12px !important;
    height: 32px !important;
    text-transform: none !important;
    letter-spacing: 0.3px;
    border-radius: 6px !important;
    min-width: auto !important;

    .v-icon {
      font-size: 14px !important;
    }

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
  }
}

// Expand/Collapse Animation
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.expand-enter,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.expand-enter-to,
.expand-leave {
  max-height: 200px;
  opacity: 1;
}

// Recent Activity Container - animates in/out smoothly
.recent-activity-container {
  padding: 8px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

// Recent Activity Badge
.recent-activity {
  padding: 8px 12px;
  background: #4caf50;
  color: white;
  border-radius: 16px;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
  white-space: nowrap;

  .activity-text {
    font-weight: 500;
    color: white;
  }
}

// Container slide animations - smooth height transitions
.activity-container-slide-enter-active,
.activity-container-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  overflow: hidden;
}

.activity-container-slide-enter {
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
  opacity: 0;
  transform: translateY(-10px);
}

.activity-container-slide-enter-to {
  max-height: 100px;
  padding-top: 8px;
  padding-bottom: 8px;
  opacity: 1;
  transform: translateY(0);
}

.activity-container-slide-leave {
  max-height: 100px;
  padding-top: 8px;
  padding-bottom: 8px;
  opacity: 1;
  transform: translateY(0);
}

.activity-container-slide-leave-to {
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
  opacity: 0;
  transform: translateY(-10px);
}

// Pulse animation
@keyframes pulse {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

// Mobile Responsive
@media (max-width: 600px) {
  .hardware-status-new {
    bottom: 10px;
    right: 10px;
    left: 10px;
    min-width: unset;
    max-width: unset;
    max-height: calc(100vh - 20px);
  }

  .categories-header {
    padding: 12px 16px 10px;
  }

  .category-row {
    padding: 10px 16px;
    min-height: 48px;
  }

  .category-icon {
    width: 32px;
    height: 32px;
    margin-right: 10px;

    .v-icon {
      font-size: 16px !important;
    }
  }

  .actions-panel {
    padding: 12px 16px 16px 58px;
  }

  .action-button {
    height: 28px !important;
    font-size: 11px !important;

    .v-icon {
      font-size: 12px !important;
    }
  }

  .recent-activity-container {
    padding: 6px 16px;
  }

  .recent-activity {
    padding: 6px 10px;
    font-size: 11px;
  }

  // Mobile container slide animations
  .activity-container-slide-enter-to,
  .activity-container-slide-leave {
    padding-top: 6px;
    padding-bottom: 6px;
  }
}

// Legacy hardware-status class for backward compatibility
.hardware-status {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: white;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  z-index: 1000;
  transition: all 0.2s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

  // Clean state colors
  &.connected {
    border-left: 3px solid #4caf50;
  }

  &.warning {
    border-left: 3px solid #ff9800;
  }

  &.disconnected,
  &.no-devices {
    border-left: 3px solid #757575;
  }

  &.initializing {
    border-left: 3px solid #e8e8e8;
    background: #fafafa;
  }
}

// Test Scale Dialog Styles
.weight-display-large {
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border: 3px solid #1976d2;
  border-radius: 12px;
  padding: 24px;
  margin: 16px 0;
  position: relative;

  .weight-value {
    font-size: 3rem;
    font-weight: 600;
    color: #1976d2;
    line-height: 1;
  }

  .weight-unit {
    font-size: 1.2rem;
    color: #666;
    margin-top: 4px;
  }

  &::after {
    content: '⚖️';
    position: absolute;
    top: 12px;
    right: 16px;
    font-size: 20px;
    opacity: 0.7;
  }
}

// Weight Log Dialog Styles
.weight-log-list {
  max-height: 300px;
  overflow-y: auto;
}

.weight-log-entry {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 8px;
  transition: background-color 0.2s ease;

  &:hover {
    background: #e9ecef;
  }

  .weight-value-small {
    font-weight: 600;
    color: #333;
    font-size: 0.95rem;
  }
}
</style>